import { Controller } from '@hotwired/stimulus';
import EmblaCarousel, { type EmblaCarouselType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import { isMobileViewport } from '../../../../utils/customer/is_mobile_viewport';

/**
 * TOPページのカルーセルを制御します
 */
export default class extends Controller<HTMLElement> {
  static override targets = [
    'emblaViewport',
    'emblaContainer',
    'emblaSlide',
    'dots',
    'dot',
    'playButton',
    'playIcon',
    'pauseIcon',
    'emblaBackgroundColor',
  ];

  declare readonly emblaViewportTarget: HTMLElement;
  declare readonly emblaContainerTarget: HTMLElement;
  declare readonly emblaSlideTargets: HTMLElement[];
  declare readonly dotsTarget: HTMLElement;
  declare readonly dotTargets: HTMLElement[];
  declare readonly playButtonTarget: HTMLButtonElement;
  declare readonly playIconTarget: HTMLElement;
  declare readonly pauseIconTarget: HTMLElement;
  declare readonly emblaBackgroundColorTarget: HTMLElement;

  private emblaApi!: EmblaCarouselType;
  private resizeHandler!: () => void;
  private DEFAULT_SCALE = 1.0;
  private DOWN_SCALE = 0.86956522;
  private readonly MIN_SLIDES_PC = 5;
  private readonly MIN_SLIDES_SP = 3;

  override connect() {
    this.adjustSlidesForLooping();

    this.initializeCarousel();
    this.updateBackgroundStyle(); // 背景の高さと位置を初期設定
    this.resizeHandler = this.updateBackgroundStyle.bind(this); // ウィンドウリサイズ時に背景の高さを更新するイベントリスナーを登録
    window.addEventListener('resize', this.resizeHandler);
  }

  override disconnect() {
    if (this.emblaApi) {
      this.emblaApi.destroy();
    }

    window.removeEventListener('resize', this.resizeHandler);
  }

  /**
   * 画面サイズに応じて、カルーセルのループ表示に必要な最低スライド数を確保します。
   * スライド数が足りない場合は、既存のスライドを複製してDOMに追加します。
   */
  private adjustSlidesForLooping() {
    const container = this.emblaContainerTarget;
    if (!container) return;

    // Stimulusが自動で解決する `this.slideTargets` はDOM変更前にキャッシュされている可能性があるため、コンテナから直接子要素を取得します。
    const originalSlides = Array.from(container.children) as HTMLElement[];
    const originalSlideCount = originalSlides.length;

    // 表示すべきイベントが1つもない場合は何もしない
    if (originalSlideCount === 0) {
      this.element.remove(); // カルーセル自体を非表示にする
      return;
    }

    const minSlides = isMobileViewport() ? this.MIN_SLIDES_SP : this.MIN_SLIDES_PC;

    // 実際のイベント数が最低枚数に満たない場合のみ、複製処理を行う
    if (originalSlideCount < minSlides) {
      let currentSlideIndex = 0;
      // スライドの総数が最低枚数に達するまでループ
      while (container.children.length < minSlides) {
        // 元のスライドを順番に参照してクローンを作成
        const slideToClone = originalSlides[currentSlideIndex % originalSlideCount];
        const clonedSlide = slideToClone?.cloneNode(true) as HTMLElement;
        // 作成したクローンをコンテナの末尾に追加
        container.appendChild(clonedSlide);
        currentSlideIndex++;
      }
    }
  }

  private initializeCarousel() {
    try {
      const autoplayPlugin = Autoplay({
        delay: 5000,
        stopOnInteraction: false, // ユーザーが操作しても再生を止めない
        stopOnMouseEnter: true, // マウスが乗ったら再生を止める
      });

      // EmblaCarouselを初期化
      this.emblaApi = EmblaCarousel(
        this.emblaViewportTarget,
        {
          loop: true,
          dragFree: false, // ドラッグ時の自由なスクロールを無効
          align: 'center',
          slidesToScroll: 1, // 一度にスクロールするスライド数
          skipSnaps: false, // スナップポイントをスキップしない
        },
        [autoplayPlugin]
      );

      // Emblaのイベントリスナーを設定
      this.emblaApi.on('select', this.updateDots.bind(this));
      this.emblaApi.on('reInit', this.updateDots.bind(this));
      this.emblaApi.on('select', this.updateSlideScales.bind(this));
      this.emblaApi.on('reInit', this.updateSlideScales.bind(this));
      this.emblaApi.on('autoplay:play', this.updatePlayButtonState.bind(this));
      this.emblaApi.on('autoplay:stop', this.updatePlayButtonState.bind(this));

      // 初期状態のボタン表示とドットを更新
      this.updateDots();
      this.updateSlideScales();
    } catch (error) {
      console.error('CarouselController: Failed to initialize EmblaCarousel', error);
    }
  }

  /**
   * 特定のスライドに移動する
   */
  goToSlide(event: Event & { params: { index?: number | string } }) {
    if (!this.emblaApi) return;
    const slideIndex = Number(event.params.index ?? 0);
    this.emblaApi.scrollTo(slideIndex);
  }

  /**
   * 再生/一時停止を切り替える
   */
  togglePlay() {
    const autoplay = this.emblaApi?.plugins().autoplay;
    if (!autoplay) return;

    if (autoplay.isPlaying()) {
      autoplay.stop();
    } else {
      autoplay.play();
    }
  }

  /**
   * 再生ボタンの状態を更新する
   */
  private updatePlayButtonState() {
    const autoplay = this.emblaApi?.plugins().autoplay;
    if (!autoplay) return;

    if (autoplay.isPlaying()) {
      this.playIconTarget.classList.remove('hidden');
      this.pauseIconTarget.classList.add('hidden');
    } else {
      this.playIconTarget.classList.add('hidden');
      this.pauseIconTarget.classList.remove('hidden');
    }
  }

  /**
   * ドットの状態を更新する
   */
  private updateDots() {
    if (!this.emblaApi || !this.dotsTarget) return;

    const selectedIndex = this.emblaApi.selectedScrollSnap();

    this.dotTargets.forEach((dot, index) => {
      if (index === selectedIndex) {
        dot.classList.add('bg-primary-400');
        dot.classList.remove('bg-primary-100', 'border', 'border-primary-25');
      } else {
        dot.classList.remove('bg-primary-400');
        dot.classList.add('bg-primary-100', 'border', 'border-primary-25');
      }
    });
  }

  /**
   * スライドのスケール（大きさ）を更新します。
   * 選択されているスライドを大きく、その他を同じサイズにします。
   */
  private updateSlideScales() {
    if (!this.emblaApi) return;

    const selectedIndex = this.emblaApi.selectedScrollSnap();
    const defaultScale = this.DEFAULT_SCALE; // 中央
    const downScale = this.DOWN_SCALE; // サイド

    this.emblaSlideTargets.forEach((slide, index) => {
      if (index === selectedIndex) {
        // 選択されたスライドを拡大し、クラスを追加
        slide.style.transform = `scale(${defaultScale})`;
        slide.classList.add('custom-shadow-3');
      } else {
        slide.style.transform = `scale(${downScale})`;
        slide.classList.remove('custom-shadow-3');
      }
    });
  }

  /**
   * 背景要素の高さを、カルーセルのスライドの高さに基づいて調整します。
   */
  private updateBackgroundStyle() {
    const firstSlide = this.emblaSlideTargets[0];
    if (!firstSlide) return;

    const slideHeight = firstSlide.offsetHeight;
    const newHeight = slideHeight * this.DOWN_SCALE;
    const topOffset = (slideHeight - slideHeight * this.DOWN_SCALE) / 2;

    this.emblaBackgroundColorTarget.style.height = `${newHeight + (isMobileViewport() ? 15 : 30)}px`;
    this.emblaBackgroundColorTarget.style.top = `${topOffset + (isMobileViewport() ? 28 : 40)}px`;
  }
}
